

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

function SidenavCollapse({ icon, name, active, route, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;

  return (
    <ListItem component="li" disablePadding sx={{ display: "block", px: 1.5 }}>
      <Tooltip title={name} placement="right" arrow enterDelay={300}>
        <ListItem
          component="div"
          disablePadding
          sx={{ display: "block" }}
        >
          <MDBox
            {...rest}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-start"
            width="100%"
            sx={(theme) =>
              collapseItem(theme, {
                active,
                transparentSidenav,
                whiteSidenav,
                darkMode,
                sidenavColor,
              })
            }
          >
            <ListItemIcon
              sx={(theme) => ({
                minWidth: 0,
                padding: 0,
                alignItems: "center",
                justifyContent: "center",
                ...collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active }),
              })}
            >
              {typeof icon === "string" ? (
                <Icon sx={(theme) => collapseIcon(theme, { active, whiteSidenav, darkMode })}>{icon}</Icon>
              ) : (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={(theme) => ({
                    color: collapseIcon(theme, { active, whiteSidenav }).color,
                    "& svg": { color: `${collapseIcon(theme, { active, whiteSidenav }).color} !important` },
                    "& *": { color: `${collapseIcon(theme, { active, whiteSidenav }).color} !important` },
                  })}
                >
                  {icon}
                </MDBox>
              )}
            </ListItemIcon>

            <ListItemText
              primary={name}
              primaryTypographyProps={{ align: "left", noWrap: true }}
              sx={(theme) => ({
                ...collapseText(theme, {
                  miniSidenav,
                  transparentSidenav,
                  whiteSidenav,
                  active,
                  darkMode,
                }),
              })}
            />
          </MDBox>
        </ListItem>
      </Tooltip>
    </ListItem>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  route: PropTypes.string,
};

export default SidenavCollapse;
